import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { TagService } from '../services/tags'
import { CategoryService, type CategoryWithTags } from '../services/categories'
import { supabase } from '../lib/supabase'
import type { VideoWithCategory } from '../services/videos'
import type { Tag } from '../services/tags'
import hashtagIcon from '../assets/icons/hashtag-solid.png'
import VideoThumbnail from '../components/VideoThumbnail'

const TagSearchPage = () => {
  const { tagName } = useParams<{ tagName: string }>()
  const [videos, setVideos] = useState<VideoWithCategory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [tag, setTag] = useState<Tag | null>(null)
  const [categories, setCategories] = useState<CategoryWithTags[]>([])
  const [relatedTags, setRelatedTags] = useState<Tag[]>([])
  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    if (tagName) {
      loadVideosByTag(tagName)
      loadCategories()
    }
  }, [tagName]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadCategories = async () => {
    try {
      console.log('Loading categories...')
      const categoriesData = await CategoryService.getCategoriesWithTags()
      console.log('Categories loaded:', categoriesData)
      setCategories(categoriesData)
    } catch (error) {
      console.error('Failed to load categories:', error)
    }
  }

  const loadRelatedTags = async (categoryId: string) => {
    try {
      const tags = await TagService.getTagsByCategory(categoryId)
      setRelatedTags(tags)
    } catch (error) {
      console.error('Failed to load related tags:', error)
    }
  }

  const loadVideosByTag = async (tagSlug: string) => {
    try {
      setIsLoading(true)
      setError('')

      // First, find the tag by slug
      const decodedTagSlug = decodeURIComponent(tagSlug)

      // Get tag by slug using TagService
      const tagData = await TagService.getTag(decodedTagSlug)

      if (!tagData || !tagData.is_active) {
        // If tag not found by slug, try to find by name as fallback
        const { data: tagByName, error: tagByNameError } = await supabase
          .from('tags')
          .select('*')
          .ilike('name', decodedTagSlug)
          .eq('is_active', true)
          .limit(1)
          .single()

        if (tagByNameError || !tagByName) {
          console.error('Tag not found by slug or name')
          setError('指定されたタグが見つかりません')
          return
        }

        setTag(tagByName)
        const tagId: string = tagByName.id

        // Load related tags from the same category
        if (tagByName.category_id) {
          await loadRelatedTags(tagByName.category_id)
        }

        // Get videos with this tag
        await getVideosByTagId(tagId)
      } else {
        setTag(tagData)
        const tagId: string = tagData.id

        // Load related tags from the same category
        if (tagData.category_id) {
          await loadRelatedTags(tagData.category_id)
        }

        // Get videos with this tag
        await getVideosByTagId(tagId)
      }
    } catch (error) {
      console.error('Failed to load videos by tag:', error)
      setError('動画の読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const getVideosByTagId = async (tagId: string) => {
    try {
      // Get videos with this tag
      const { data: videoTagsData, error: videoTagsError } = await supabase
        .from('video_tags')
        .select(`
          video_id,
          videos!inner (
            *,
            categories!category_id (
              id,
              name,
              slug
            ),
            video_tags (
              tags (
                id,
                name,
                slug
              )
            )
          )
        `)
        .eq('tag_id', tagId)

      if (videoTagsError) {
        console.error('Failed to load videos:', videoTagsError)
        setError('動画の読み込みに失敗しました')
        return
      }

      // Transform the data
      const transformedVideos: VideoWithCategory[] = (videoTagsData || []).map((vt: {
        video_id: string;
        videos: any
      }) => ({
        ...vt.videos,
        categories: vt.videos.categories || null,
        tags: vt.videos.video_tags?.map((vtag: { tags: { name: string; slug: string } }) => ({
          name: vtag.tags.name,
          slug: vtag.tags.slug
        })) || []
      }))

      setVideos(transformedVideos)
    } catch (error) {
      console.error('Failed to load videos by tag ID:', error)
      throw error
    }
  }

  const VideoCard = ({ video }: { video: VideoWithCategory }) => (
    <Link
      to={`/video/${video.id}`}
      className="block group"
    >
      <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
        <div className="relative">
          <VideoThumbnail
            thumbnailUrl={video.thumbnail_url}
            title={video.title}
            className="w-full aspect-video"
            thumbnailTitle={video.thumbnail_title}
            schoolOriginal={video.is_school_video && video.video_type === 'chat_only'}
          />
          {video.duration && (
            <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
              {video.duration}
            </div>
          )}
        </div>
        <div className="p-3">
          <h3 className="font-bold text-sm mb-1 line-clamp-2" style={{ color: '#da4574' }}>
            {video.title}
          </h3>

          {/* Tags */}
          {!video.is_school_video && (
            <div className="flex flex-wrap gap-2 mb-2">
              {video.tags && Array.isArray(video.tags) && video.tags.length > 0 ? (
                (video.tags as Array<{ name: string; slug: string }>).slice(0, 3).map(tagItem => (
                  <Link
                    key={tagItem.slug}
                    to={`/tags/${encodeURIComponent(tagItem.slug)}`}
                    className="flex items-center gap-1 text-xs hover:bg-gray-100 rounded px-1 py-0.5 transition-colors"
                  >
                    <img src={hashtagIcon} alt="#" className="w-3 h-3" />
                    <span className="font-semibold text-gray-800">{tagItem.name}</span>
                  </Link>
                ))
              ) : (
                <span className="text-xs text-gray-400">タグなし</span>
              )}
            </div>
          )}

          {/* Category */}
          {!video.is_school_video && video.categories && (
            <div className="text-xs text-gray-600">
              カテゴリ: {video.categories.name}
            </div>
          )}
        </div>
      </div>
    </Link>
  )

  return (
    <div className="flex">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 md:hidden z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Left Sidebar */}
        <div className={`fixed inset-y-0 left-0 w-64 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:static md:inset-0 transition-transform duration-300 ease-in-out z-50 md:z-0`}>
          <div className="h-full overflow-y-auto p-4">
            <div className="bg-white rounded-lg shadow-lg p-4 h-full">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">カテゴリ・タグ</h2>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Categories with Tags */}
              <div className="space-y-3">
                {categories.map((category) => {
                  const hasActiveTag = category.tags.some(categoryTag => tag?.slug === categoryTag.slug)
                  return (
                    <div key={category.id} className="border-b border-gray-200 pb-3">
                      <h3 className={`font-medium mb-2 text-sm ${
                        hasActiveTag ? 'text-[#da4574]' : 'text-gray-900'
                      }`}>{category.name}</h3>
                    <div className="space-y-1">
                      {category.tags.map((categoryTag) => {
                        const isActive = tag?.slug === categoryTag.slug
                        return (
                          <Link
                            key={categoryTag.id}
                            to={`/tags/${encodeURIComponent(categoryTag.slug)}`}
                            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-gray-100`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <img src={hashtagIcon} alt="#" className="w-3 h-3" />
                            <span className={`text-sm font-semibold ${
                              isActive ? 'text-[#da4574]' : 'text-gray-800'
                            }`}>{categoryTag.name}</span>
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 md:ml-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6">
            {/* Mobile menu button */}
            <div className="md:hidden mb-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-md shadow-sm border text-gray-700 hover:bg-gray-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                カテゴリ・タグ
              </button>
            </div>

            {/* Tag Header */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <img src={hashtagIcon} alt="#" className="w-8 h-8" />
                <h1 className="text-3xl font-bold text-gray-900">
                  {tag?.name || decodeURIComponent(tagName || '')}
                </h1>
              </div>

              {!isLoading && !error && (
                <p className="text-gray-600">
                  {videos.length}件の動画が見つかりました
                </p>
              )}
            </div>

            {/* Related Tags - Same Category */}
            {relatedTags.length > 0 && (
              <div className="mb-6">
                <div className="bg-white rounded-lg shadow-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">関連タグ</h3>
                  <div className="flex flex-wrap gap-2 overflow-x-auto pb-2">
                    {relatedTags.map((relatedTag) => {
                      const isActive = tag?.slug === relatedTag.slug
                      return (
                        <Link
                          key={relatedTag.id}
                          to={`/tags/${encodeURIComponent(relatedTag.slug)}`}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-full whitespace-nowrap transition-colors hover:bg-gray-100 border border-gray-200"
                        >
                          <img src={hashtagIcon} alt="#" className="w-3 h-3" />
                          <span className={`text-sm font-semibold ${
                            isActive ? 'text-[#da4574]' : 'text-gray-800'
                          }`}>{relatedTag.name}</span>
                        </Link>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#da4574]"></div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                {error}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && videos.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🎬</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  動画が見つかりません
                </h3>
                <p className="text-gray-600 mb-4">
                  「{tag?.name || decodeURIComponent(tagName || '')}」のタグが付いた動画はありません。
                </p>
                <Link
                  to="/videos"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#da4574] hover:bg-[#c63a68]"
                >
                  すべての動画を見る
                </Link>
              </div>
            )}

            {/* Videos Grid */}
            {!isLoading && !error && videos.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {videos.map(video => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
  )
}

export default TagSearchPage